import React, { useState } from 'react';
import CalendarPermissionsManager from './CalendarPermissionsManager';
import LockedTimeManager from './LockedTimeManager';

const SchedulingSettings = () => {
  const [activeTab, setActiveTab] = useState('permissions');

  const tabs = [
    {
      id: 'permissions',
      name: 'Calendar Permissions',
      icon: '👥',
      description: 'Manage who can schedule on your calendar'
    },
    {
      id: 'blocked-time',
      name: 'Blocked Time',
      icon: '⏰',
      description: 'Manage your unavailable time periods'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Scheduling Settings</h1>
          <p className="text-gray-600 mt-2">
            Configure calendar permissions and blocked time periods for intelligent scheduling
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-yellow-500 text-yellow-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Descriptions */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <p className="text-sm text-gray-600">
              {tabs.find(tab => tab.id === activeTab)?.description}
            </p>
          </div>

          {/* Tab Content */}
          <div className="bg-white">
            {activeTab === 'permissions' && <CalendarPermissionsManager />}
            {activeTab === 'blocked-time' && <LockedTimeManager />}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            📚 About Intelligent Scheduling
          </h3>
          <div className="text-gray-800 space-y-2">
            <p>
              <strong>Calendar Permissions:</strong> Control who can schedule meetings and tasks on your calendar. 
              Set different permission levels for team members based on their role and your preferences.
            </p>
            <p>
              <strong>Blocked Time:</strong> Define time periods when you're unavailable for scheduling. 
              This includes lunch breaks, personal time, and recurring commitments that should be protected.
            </p>
            <p>
              <strong>Conflict Detection:</strong> The system automatically detects scheduling conflicts 
              between calendar events and blocked time periods, helping maintain your schedule integrity.
            </p>
          </div>
        </div>

        {/* Phase Information */}
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <span className="text-green-600 font-medium">✅ Phase 2 Complete:</span>
            <span className="text-green-800">Calendar Permissions & Blocked Time Management</span>
          </div>
          <div className="mt-2 text-sm text-green-700">
            <strong>Next Phase:</strong> Visual conflict resolution and auto-scheduling algorithms
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulingSettings;
